import React, { useState, useEffect, useRef } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useToast } from '../../contexts/ToastContext'
import * as api from '../../lib/api'
import Modal from '../../components/Modal'
import CustomSelect from '../../components/CustomSelect/CustomSelect'
import {
  IconPlus,
  IconSend,
  IconCheck,
  IconClock,
  IconAlert,
  IconPaperclip,
  IconChevronDown,
  IconUser,
  IconBuilding,
  IconSearch,
  IconRefresh
} from '../../components/Icons'
import './SupportPage.css'

const CATEGORY_OPTIONS = [
  { value: 'question', label: 'Вопрос по работе' },
  { value: 'bug', label: 'Ошибка / Замечание' },
  { value: 'feature', label: 'Идея / Пожелание' },
  { value: 'billing', label: 'Оплата и Тарифы' }
]

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Низкий' },
  { value: 'medium', label: 'Средний' },
  { value: 'high', label: 'Высокий' },
  { value: 'urgent', label: 'Срочный' }
]

const STATUS_OPTIONS = [
  { value: 'open', label: 'Статус: Новое' },
  { value: 'in_progress', label: 'Статус: В работе' },
  { value: 'answered', label: 'Статус: Отвечено' },
  { value: 'closed', label: 'Статус: Закрыто' }
]

export default function SupportPage() {
  const { profile } = useAuth()
  const { showToast } = useToast()
  const messagesEndRef = useRef(null)

  const isSuperAdmin = profile?.role === 'superadmin' || profile?.is_superadmin === true

  const [tickets, setTickets] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedTicketId, setSelectedTicketId] = useState(null)
  const [activeTicket, setActiveTicket] = useState(null)
  const [messages, setMessages] = useState([])
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [replyText, setReplyText] = useState('')
  const [sendingReply, setSendingReply] = useState(false)

  // Поиск и фильтры
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  // Модалка нового тикета
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [newCategory, setNewCategory] = useState('question')
  const [newPriority, setNewPriority] = useState('medium')
  const [newMessage, setNewMessage] = useState('')
  const [attachmentUrl, setAttachmentUrl] = useState('')
  const [uploadingFile, setUploadingFile] = useState(false)
  const [submittingTicket, setSubmittingTicket] = useState(false)

  useEffect(() => {
    loadTickets()
  }, [])

  useEffect(() => {
    if (selectedTicketId) {
      loadTicketDetails(selectedTicketId)
    }
  }, [selectedTicketId])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const loadTickets = async () => {
    setLoading(true)
    try {
      const list = await api.getSupportTickets()
      setTickets(list)
      if (list.length > 0 && !selectedTicketId) {
        setSelectedTicketId(list[0].id)
      }
    } catch (err) {
      console.error('Failed to load tickets', err)
      showToast('Ошибка загрузки обращений', 'error')
    } finally {
      setLoading(false)
    }
  }

  const loadTicketDetails = async (ticketId) => {
    setLoadingDetails(true)
    try {
      const res = await api.getSupportTicketDetails(ticketId)
      if (res.success) {
        setActiveTicket(res.ticket)
        setMessages(res.messages || [])
      }
    } catch (err) {
      console.error('Failed to load ticket details', err)
      showToast('Ошибка загрузки деталей обращения', 'error')
    } finally {
      setLoadingDetails(false)
    }
  }

  const handleSendReply = async (e) => {
    e?.preventDefault()
    if (!replyText.trim() || !selectedTicketId || sendingReply) return

    setSendingReply(true)
    try {
      const res = await api.sendSupportTicketMessage(selectedTicketId, {
        message: replyText.trim()
      })
      if (res.success) {
        setReplyText('')
        await loadTicketDetails(selectedTicketId)
        await loadTickets()
        showToast('Ответ отправлен', 'success')
      }
    } catch (err) {
      console.error('Failed to send reply', err)
      showToast('Ошибка отправки ответа', 'error')
    } finally {
      setSendingReply(false)
    }
  }

  const handleChangeStatus = async (newStatus) => {
    if (!selectedTicketId) return
    try {
      const res = await api.updateSupportTicketStatus(selectedTicketId, newStatus)
      if (res.success) {
        showToast('Статус обновлён', 'success')
        await loadTicketDetails(selectedTicketId)
        await loadTickets()
      }
    } catch (err) {
      console.error('Failed to change status', err)
      showToast('Ошибка изменения статуса', 'error')
    }
  }

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploadingFile(true)
    try {
      const res = await api.uploadEvidenceFile(file)
      if (res?.url) {
        setAttachmentUrl(res.url)
        showToast('Файл успешно прикреплен', 'success')
      }
    } catch (err) {
      console.error('Upload error', err)
      showToast('Ошибка загрузки файла', 'error')
    } finally {
      setUploadingFile(false)
    }
  }

  const handleCreateTicket = async (e) => {
    e?.preventDefault()
    if (!newTitle.trim() || submittingTicket) return

    setSubmittingTicket(true)
    try {
      const res = await api.createSupportTicket({
        title: newTitle.trim(),
        category: newCategory,
        priority: newPriority,
        message: newMessage.trim(),
        attachment_url: attachmentUrl || null
      })

      if (res.success) {
        showToast('Обращение создано!', 'success')
        setIsModalOpen(false)
        setNewTitle('')
        setNewMessage('')
        setAttachmentUrl('')
        await loadTickets()
        if (res.ticket?.id) {
          setSelectedTicketId(res.ticket.id)
        }
      }
    } catch (err) {
      console.error('Create ticket error', err)
      showToast('Ошибка создания обращения', 'error')
    } finally {
      setSubmittingTicket(false)
    }
  }

  // Фильтрация
  const filteredTickets = tickets.filter(t => {
    const matchesSearch = (t.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (t.org_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (t.user_name || '').toLowerCase().includes(searchQuery.toLowerCase())
    const matchesStatus = statusFilter === 'all' || t.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return <span className="support-badge support-badge--open">Новое</span>
      case 'in_progress':
        return <span className="support-badge support-badge--progress">В работе</span>
      case 'answered':
        return <span className="support-badge support-badge--answered">Отвечено</span>
      case 'closed':
        return <span className="support-badge support-badge--closed">Закрыто</span>
      default:
        return <span className="support-badge">{status}</span>
    }
  }

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'bug': return 'Ошибка / Баг'
      case 'question': return 'Вопрос по работе'
      case 'feature': return 'Идея / Пожелание'
      case 'billing': return 'Оплата и Тарифы'
      default: return 'Общий вопрос'
    }
  }

  const [showInfoBanner, setShowInfoBanner] = useState(true)

  return (
    <div className="support-page animate-fade-in">
      <div className="support-header">
        <div>
          <h1 className="support-title">Служба поддержки ПВЗ</h1>
          <p className="support-subtitle">
            {isSuperAdmin
              ? 'Единый центр управления обращениями пользователей со всех пунктов выдачи'
              : 'Официальная техподдержка и обратная связь для владельцев и сотрудников ПВЗ'}
          </p>
        </div>
        <div className="support-header-actions">
          <button className="support-btn-refresh" onClick={loadTickets} title="Обновить список">
            <IconRefresh size={16} />
          </button>
          <button className="support-btn-create" onClick={() => setIsModalOpen(true)}>
            <IconPlus size={16} style={{ marginRight: 6 }} />
            Новое обращение
          </button>
        </div>
      </div>

      {/* Информационный карточка-гайд: Для чего этот раздел */}
      {showInfoBanner && (
        <div style={{
          background: 'var(--surface-color, #ffffff)',
          border: '1px solid var(--border-color, rgba(0,0,0,0.08))',
          borderRadius: '16px',
          padding: '18px 20px',
          marginBottom: '20px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.03)',
          position: 'relative'
        }}>
          <button 
            onClick={() => setShowInfoBanner(false)}
            style={{
              position: 'absolute',
              top: '14px',
              right: '16px',
              border: 'none',
              background: 'transparent',
              color: 'var(--text-tertiary, #94a3b8)',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600
            }}
          >
            ✕ Скрыть
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            <span style={{ fontSize: '20px' }}>ℹ️</span>
            <div>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 800, color: 'var(--text-primary)' }}>
                Как работает Поддержка и кому предназначен этот раздел?
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: '12.5px', color: 'var(--text-secondary)' }}>
                Официальный канал связи с технической командой платформы ПВЗ Расчёт
              </p>
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '12px',
            marginTop: '12px'
          }}>
            <div style={{
              background: 'var(--bg-secondary, #f8fafc)',
              padding: '12px 14px',
              borderRadius: '12px',
              border: '1px solid var(--border-color, #e2e8f0)'
            }}>
              <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text-primary)', marginBottom: '4px' }}>
                👥 Для кого создан раздел?
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                • <b>Владельцы ПВЗ:</b> вопросы по тарифам, расчёту выплат, интеграциям (камеры, Яндекс.Диск).<br />
                • <b>Сотрудники ПВЗ:</b> помощь по функционалу смен, графику и ошибкам в работе.
              </div>
            </div>

            <div style={{
              background: 'var(--bg-secondary, #f8fafc)',
              padding: '12px 14px',
              borderRadius: '12px',
              border: '1px solid var(--border-color, #e2e8f0)'
            }}>
              <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text-primary)', marginBottom: '4px' }}>
                💬 С чем можно обращаться?
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                • Сообщение об ошибке или сбое в работе.<br />
                • Предложение новой функции для вашего ПВЗ.<br />
                • Вопросы по подпискам и настройкам выплат.
              </div>
            </div>

            <div style={{
              background: 'var(--bg-secondary, #f8fafc)',
              padding: '12px 14px',
              borderRadius: '12px',
              border: '1px solid var(--border-color, #e2e8f0)'
            }}>
              <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text-primary)', marginBottom: '4px' }}>
                🛡 Гарантия ответа
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                Каждое обращение фиксируется в системе. Технические специалисты и администраторы ответят вам прямо в чате обращения.
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="support-layout">
        {/* Левая колонка: Список тикетов */}
        <div className="support-sidebar">
          <div className="support-search-bar">
            <IconSearch size={16} className="support-search-icon" />
            <input
              type="text"
              placeholder="Поиск обращений..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="support-search-input"
            />
          </div>

          <div className="support-filter-tabs">
            <button
              className={`support-filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              Все
            </button>
            <button
              className={`support-filter-btn ${statusFilter === 'open' ? 'active' : ''}`}
              onClick={() => setStatusFilter('open')}
            >
              Новые
            </button>
            <button
              className={`support-filter-btn ${statusFilter === 'answered' ? 'active' : ''}`}
              onClick={() => setStatusFilter('answered')}
            >
              Отвечены
            </button>
            <button
              className={`support-filter-btn ${statusFilter === 'closed' ? 'active' : ''}`}
              onClick={() => setStatusFilter('closed')}
            >
              Закрыты
            </button>
          </div>

          <div className="support-ticket-list">
            {loading ? (
              <div className="support-empty-state">Загрузка обращений...</div>
            ) : filteredTickets.length === 0 ? (
              <div className="support-empty-state">Обращений не найдено</div>
            ) : (
              filteredTickets.map(t => {
                const isSelected = t.id === selectedTicketId
                return (
                  <div
                    key={t.id}
                    className={`support-ticket-card ${isSelected ? 'is-selected' : ''}`}
                    onClick={() => setSelectedTicketId(t.id)}
                  >
                    <div className="support-ticket-card-header">
                      <span className="support-category-tag">{getCategoryLabel(t.category)}</span>
                      {getStatusBadge(t.status)}
                    </div>
                    <h4 className="support-ticket-card-title">{t.title}</h4>
                    {isSuperAdmin && (
                      <div className="support-ticket-meta">
                        <IconBuilding size={12} />
                        <span>{t.org_name || 'ПВЗ'}</span>
                        <span className="support-meta-divider">•</span>
                        <IconUser size={12} />
                        <span>{t.user_name || 'Пользователь'}</span>
                      </div>
                    )}
                    <div className="support-ticket-card-footer">
                      <span className="support-ticket-time">
                        {t.updated_at ? new Date(t.updated_at).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                      <span className="support-msg-count">{t.messages_count || 1} сообщ.</span>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>

        {/* Правая колонка: Чат выбранного тикета */}
        <div className="support-main">
          {!selectedTicketId || !activeTicket ? (
            <div className="support-no-selection">
              <IconAlert size={36} style={{ opacity: 0.5, marginBottom: 12 }} />
              <h3>Выберите обращение слева</h3>
              <p>или создайте новое обращение в службу поддержки</p>
            </div>
          ) : (
            <div className="support-chat-container">
              {/* Шапка тикета */}
              <div className="support-chat-header">
                <div>
                  <div className="support-chat-tags">
                    <span className="support-category-tag">{getCategoryLabel(activeTicket.category)}</span>
                    {getStatusBadge(activeTicket.status)}
                  </div>
                  <h2 className="support-chat-title">{activeTicket.title}</h2>
                  <div className="support-chat-submeta">
                    <span>Автор: <strong>{activeTicket.user_name}</strong></span>
                    {activeTicket.org_name && <span> | ПВЗ: <strong>{activeTicket.org_name}</strong></span>}
                    <span> | {new Date(activeTicket.created_at).toLocaleString('ru-RU')}</span>
                  </div>
                </div>

                <div className="support-chat-actions" style={{ minWidth: '180px' }}>
                  <CustomSelect
                    options={STATUS_OPTIONS}
                    value={activeTicket.status}
                    onChange={handleChangeStatus}
                  />
                </div>
              </div>

              {/* Лента сообщений */}
              <div className="support-messages-stream">
                {loadingDetails ? (
                  <div className="support-empty-state">Загрузка переписки...</div>
                ) : (
                  messages.map((m, idx) => {
                    const isAdmin = m.is_admin_reply
                    return (
                      <div
                        key={m.id || idx}
                        className={`support-message-bubble ${isAdmin ? 'support-message-bubble--admin' : 'support-message-bubble--user'}`}
                      >
                        <div className="support-message-header">
                          <span className="support-sender-name">
                            {isAdmin ? '🛡️ Служба поддержки' : m.sender_name || 'Пользователь'}
                          </span>
                          <span className="support-message-time">
                            {new Date(m.created_at).toLocaleString('ru-RU', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}
                          </span>
                        </div>
                        <p className="support-message-body">{m.message}</p>
                        {m.attachment_url && (
                          <div className="support-attachment">
                            <a href={m.attachment_url} target="_blank" rel="noreferrer">
                              📎 Прикреплённый файл / скриншот
                            </a>
                          </div>
                        )}
                      </div>
                    )
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Поле ввода ответа */}
              {activeTicket.status === 'closed' ? (
                <div className="support-closed-notice">
                  Обращение закрыто. Вы можете изменить статус выше, чтобы продолжить переписку.
                </div>
              ) : (
                <form className="support-reply-form" onSubmit={handleSendReply}>
                  <textarea
                    rows={2}
                    placeholder="Напишите ответ или уточнение..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    className="support-reply-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        handleSendReply()
                      }
                    }}
                  />
                  <button
                    type="submit"
                    className="support-reply-send-btn"
                    disabled={sendingReply || !replyText.trim()}
                  >
                    <IconSend size={16} />
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Модалка создания тикета */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Создать новое обращение в поддержку"
      >
        <form className="support-create-form" onSubmit={handleCreateTicket}>
          <div className="support-form-group">
            <label className="support-form-label">Тема обращения *</label>
            <input
              type="text"
              required
              placeholder="Коротко опишите суть..."
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="support-form-input"
            />
          </div>

          <div className="support-form-row">
            <div className="support-form-group" style={{ flex: 1 }}>
              <CustomSelect
                label="Категория"
                options={CATEGORY_OPTIONS}
                value={newCategory}
                onChange={setNewCategory}
              />
            </div>

            <div className="support-form-group" style={{ flex: 1 }}>
              <CustomSelect
                label="Приоритет"
                options={PRIORITY_OPTIONS}
                value={newPriority}
                onChange={setNewPriority}
              />
            </div>
          </div>

          <div className="support-form-group">
            <label className="support-form-label">Подробное описание</label>
            <textarea
              rows={4}
              placeholder="Расскажите подробнее, что произошло или какую функцию вы хотите добавить..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              className="support-form-textarea"
            />
          </div>

          <div className="support-form-group">
            <label className="support-form-label">Прикрепить фото / файл (необязательно)</label>
            <div className="support-file-upload">
              <input type="file" onChange={handleFileUpload} id="ticket-file-input" hidden />
              <label htmlFor="ticket-file-input" className="support-file-upload-btn">
                <IconPaperclip size={15} style={{ marginRight: 6 }} />
                {uploadingFile ? 'Загрузка...' : attachmentUrl ? 'Файл прикреплён ✓' : 'Выбрать файл'}
              </label>
            </div>
          </div>

          <div className="support-form-actions">
            <button
              type="button"
              className="support-btn-cancel"
              onClick={() => setIsModalOpen(false)}
            >
              Отмена
            </button>
            <button
              type="submit"
              className="support-btn-submit"
              disabled={submittingTicket || !newTitle.trim()}
            >
              {submittingTicket ? 'Отправка...' : 'Отправить обращение'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
